import styles from './index.module.css'

import { useTranslation } from '@disco/i18n/use-translation'
import { IoClose } from '@react-icons/all-files/io5/IoClose'
import { Button, Flex } from '@radix-ui/themes'
import React from 'react'

import type { LinkInfoProvider } from 'types/search-page'

import AppleMusicLogo from './assets/apple-music-logo.svg'
import DiscoLogo from './assets/disco-logo.svg'
import SpotifyLogo from './assets/spotify-logo.svg'
import YoutubeLogo from './assets/youtube-logo.svg'

type Props = {
  provider: LinkInfoProvider
  title: string
  onDismiss: () => void
  dismissLabel?: string
}

const providerIcons: Record<
  LinkInfoProvider,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  spotify: SpotifyLogo,
  youtube: YoutubeLogo,
  applemusic: AppleMusicLogo,
  disco: DiscoLogo,
}

const providerNames: Record<LinkInfoProvider, string> = {
  spotify: 'Spotify',
  youtube: 'YouTube',
  applemusic: 'Apple Music',
  disco: 'DISCO',
}

/**
 * Pill shown in place of search text once a pasted link has been recognised:
 * the link source's logo, the track title and a dismiss button. Used by the
 * in-app search bar, Browse and the catalog header.
 */
const LinkPill = ({
  provider,
  title,
  onDismiss,
  dismissLabel = 'Dismiss link preview',
}: Props): React.ReactNode => {
  const { t } = useTranslation('catalogs')
  const ProviderIcon = providerIcons[provider]

  return (
    <Flex className={styles.container} title={title}>
      {ProviderIcon && (
        <span className={styles.providerIcon} data-provider={provider}>
          <ProviderIcon />
        </span>
      )}
      <span className={styles.title}>{title}</span>
      {/* Replaces the title when the search input is too narrow for a
          truncated title to mean anything */}
      <span className={styles.shortTitle}>
        {t('catalogs:providerLink', '{{provider}} link', {
          provider: providerNames[provider],
        })}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="1"
        className={styles.dismissButton}
        onClick={onDismiss}
        aria-label={dismissLabel}
      >
        <IoClose size={14} />
      </Button>
    </Flex>
  )
}

export default LinkPill
